<phdsoft-sideboard
  class="risk-shield-sideboard"
  [class.is-maximized]="isMaximized"
  [options]="optionsWindow"
  (cancel)="handleCancel()"
  (maximizeChange)="handleMaximize($event)"
>
  <div
    content
    class="rs-content"
    (input)="scheduleStateRefresh()"
    (change)="scheduleStateRefresh()"
    (click)="scheduleStateRefresh()"
  >
    <div *ngIf="isLoading || !summaries; else loaded" class="rs-state" role="status" aria-live="polite">
      <mat-spinner diameter="24"></mat-spinner>
      <span>Loading risk management settings...</span>
    </div>

    <ng-template #loaded>
      <div class="rs-layout" [class.is-rail-collapsed]="isRailCollapsed">
        <nav *ngIf="isMaximized && !isRailCollapsed" class="rs-rail" aria-label="Risk management sections">
          <ng-container *ngTemplateOutlet="sectionList; context: { $implicit: 'rail' }"></ng-container>
        </nav>

        <nav *ngIf="!isMaximized && !activeSection" class="rs-hub" aria-label="Risk management sections">
          <ng-container *ngTemplateOutlet="sectionList; context: { $implicit: 'hub' }"></ng-container>
        </nav>

        <section
          *ngIf="activeSection"
          class="rs-detail"
          [attr.aria-labelledby]="'rs-section-title-' + activeSection"
        >
          <header class="rs-detail-header">
            <div class="rs-detail-heading">
              <button
                *ngIf="!isMaximized"
                mat-icon-button
                type="button"
                class="rs-icon-button rs-back-button"
                aria-label="Back to all sections"
                title="All sections"
                (click)="backToOverview()"
              >
                <mat-icon>arrow_back</mat-icon>
              </button>
              <button
                *ngIf="isMaximized"
                mat-icon-button
                type="button"
                class="rs-icon-button rs-rail-toggle"
                [attr.aria-label]="isRailCollapsed ? 'Show sections' : 'Hide sections'"
                [attr.aria-expanded]="!isRailCollapsed"
                [title]="isRailCollapsed ? 'Show sections' : 'Hide sections'"
                (click)="isRailCollapsed = !isRailCollapsed"
              >
                <mat-icon>{{ isRailCollapsed ? 'menu' : 'menu_open' }}</mat-icon>
              </button>
              <h2 class="rs-detail-title" [id]="'rs-section-title-' + activeSection">
                {{ activeSectionInfo?.name }}
              </h2>
              <select
                *ngIf="isMaximized"
                class="rs-select rs-section-select"
                aria-label="Section"
                [ngModel]="activeSection"
                (ngModelChange)="openSection($event)"
              >
                <option *ngFor="let section of navigationSections" [ngValue]="section.id">
                  {{ section.name }}
                </option>
              </select>
              <ng-container *ngIf="!isMaximized">
                <span class="rs-counter">{{ activeSectionIndex + 1 }}/{{ navigationSections.length }}</span>
                <button
                  mat-icon-button
                  type="button"
                  class="rs-icon-button"
                  aria-label="Previous section"
                  title="Previous section"
                  [disabled]="activeSectionIndex === 0"
                  (click)="openAdjacentSection(-1)"
                >
                  <mat-icon>chevron_left</mat-icon>
                </button>
                <button
                  mat-icon-button
                  type="button"
                  class="rs-icon-button"
                  aria-label="Next section"
                  title="Next section"
                  [disabled]="activeSectionIndex === navigationSections.length - 1"
                  (click)="openAdjacentSection(1)"
                >
                  <mat-icon>chevron_right</mat-icon>
                </button>
              </ng-container>
            </div>
            <p *ngIf="!(isMaximized && isRailCollapsed)" class="rs-detail-description">{{ activeSectionInfo?.description }}</p>
          </header>

          <div class="rs-detail-body" [ngSwitch]="activeSection">
            <!-- Decision matrix -->
            <div *ngSwitchCase="'matrix'" class="rs-matrix">
              <p *ngIf="!matrixLevels.length || !allActions.length || !actionsColumns.length; else matrixContent" class="rs-empty">
                Configure risk levels, actions and causes in Risk Setup to build the decision matrix.
              </p>

              <ng-template #matrixContent>
                <div class="rs-matrix-toolbar">
                  <div class="rs-level-tabs" role="tablist" aria-label="Risk level">
                    <button
                      *ngFor="let level of matrixLevels; let i = index"
                      type="button"
                      role="tab"
                      class="rs-level-tab"
                      [class.is-active]="i === selectedLevelIndex"
                      [attr.aria-selected]="i === selectedLevelIndex"
                      [style.--rs-level-color]="level.color"
                      [title]="(levelPrescriptionCounts[level.id] || 0) + ' prescriptions'"
                      (click)="selectLevel(i)"
                    >
                      <span class="rs-level-dot" [style.background]="level.color"></span>
                      <span class="rs-level-name">{{ level.name }}</span>
                      <span class="rs-level-count">
                        {{ levelPrescriptionCounts[level.id] || 0 }}<ng-container *ngIf="!isMaximized"> {{ levelPrescriptionCounts[level.id] === 1 ? 'action' : 'actions' }}</ng-container>
                      </span>
                    </button>
                  </div>

                  <div *ngIf="isMaximized" class="rs-matrix-tools">
                    <div class="rs-pills" role="group" aria-label="Action category">
                      <button
                        *ngFor="let filter of actionFilters"
                        type="button"
                        class="rs-pill"
                        [class.is-active]="matrixActionFilter === filter.id"
                        [attr.aria-pressed]="matrixActionFilter === filter.id"
                        (click)="setActionFilter(filter.id)"
                      >
                        {{ filter.label }}
                      </button>
                    </div>
                    <select
                      #copyLevelSelect
                      class="rs-select rs-copy-level"
                      aria-label="Copy prescriptions from another level"
                      (change)="requestCopyLevel(copyLevelSelect.value); copyLevelSelect.value = ''"
                    >
                      <option value="">Copy from level…</option>
                      <ng-container *ngFor="let level of matrixLevels; let i = index">
                        <option *ngIf="i !== selectedLevelIndex" [value]="level.id">{{ level.name }}</option>
                      </ng-container>
                    </select>
                  </div>
                </div>

                <!-- Compact: one cause at a time -->
                <div *ngIf="!isMaximized" class="rs-matrix-compact">
                  <div class="rs-block rs-cause-picker">
                    <label class="rs-field-label" for="rs-matrix-cause">Cause</label>
                    <div class="rs-cause-nav">
                      <button
                        mat-icon-button
                        type="button"
                        class="rs-icon-button"
                        aria-label="Previous cause"
                        title="Previous cause"
                        (click)="openAdjacentCause(-1)"
                      >
                        <mat-icon>chevron_left</mat-icon>
                      </button>
                      <select
                        id="rs-matrix-cause"
                        class="rs-select"
                        [ngModel]="matrixCauseName"
                        (ngModelChange)="setMatrixCause($event)"
                      >
                        <optgroup *ngFor="let group of matrixCauseGroups" [label]="group.name">
                          <option *ngFor="let cause of group.causes" [ngValue]="cause.name">{{ cause.name }}</option>
                        </optgroup>
                      </select>
                      <button
                        mat-icon-button
                        type="button"
                        class="rs-icon-button"
                        aria-label="Next cause"
                        title="Next cause"
                        (click)="openAdjacentCause(1)"
                      >
                        <mat-icon>chevron_right</mat-icon>
                      </button>
                    </div>
                    <p class="rs-hint">
                      {{ countPrescribedActions(matrixCauseName) }} of {{ allActions.length }} actions prescribed ·
                      cause {{ matrixCauseIndex + 1 }} of {{ actionsColumns.length }}
                    </p>
                  </div>

                  <section *ngFor="let group of matrixActionGroups" class="rs-action-group" [attr.aria-label]="group.name">
                    <h3 class="rs-group-title">{{ group.name }}</h3>
                    <ul class="rs-action-list">
                      <ng-container *ngFor="let action of group.actions">
                        <li
                          *ngIf="getCell(action.id, matrixCauseName) as cell"
                          class="rs-action-item"
                          [class.is-open]="isPrescribed(cell) && isCompactActionOpen(action.id)"
                        >
                          <div class="rs-action-line">
                            <mat-checkbox
                              class="rs-checkbox"
                              [aria-label]="'Prescribe ' + action.actionName"
                              [checked]="isPrescribed(cell)"
                              (change)="onCompactPrescribeChange(action.id, $event.checked)"
                            ></mat-checkbox>
                            <button
                              type="button"
                              class="rs-action-toggle"
                              [disabled]="!isPrescribed(cell)"
                              [attr.aria-expanded]="isPrescribed(cell) ? isCompactActionOpen(action.id) : null"
                              (click)="toggleCompactAction(action.id)"
                            >
                              <span class="rs-action-text">
                                <span class="rs-action-name">{{ action.actionName }}</span>
                                <span *ngIf="isPrescribed(cell)" class="rs-action-summary">{{ getCellSummary(cell) }}</span>
                              </span>
                              <mat-icon *ngIf="isPrescribed(cell)" aria-hidden="true">
                                {{ isCompactActionOpen(action.id) ? 'expand_less' : 'expand_more' }}
                              </mat-icon>
                            </button>
                          </div>
                          <div *ngIf="isPrescribed(cell) && isCompactActionOpen(action.id)" class="rs-action-editor">
                            <ng-container *ngTemplateOutlet="cellEditor; context: { $implicit: cell }"></ng-container>
                          </div>
                        </li>
                      </ng-container>
                    </ul>
                  </section>
                </div>

                <!-- Maximized: full grid with inspector -->
                <div *ngIf="isMaximized" class="rs-matrix-grid">
                  <div class="rs-grid-main">
                    <div class="rs-table-scroll">
                      <table class="rs-table rs-grid-table" role="grid" [attr.aria-label]="'Decision matrix, ' + activeLevel?.name">
                        <thead>
                          <tr class="rs-group-row">
                            <th rowspan="2" scope="col" class="rs-corner">Cause \ Action</th>
                            <ng-container *ngFor="let group of matrixActionGroups">
                              <th
                                *ngIf="matrixActionFilter === 'all' || matrixActionFilter === group.id"
                                scope="colgroup"
                                class="rs-group-header is-group-start"
                                [attr.colspan]="group.actions.length"
                              >
                                {{ group.name }}
                              </th>
                            </ng-container>
                          </tr>
                          <tr class="rs-action-row">
                            <th
                              *ngFor="let action of matrixVisibleActions"
                              scope="col"
                              class="rs-action-header"
                              [class.is-group-start]="isActionGroupStart(action)"
                              [class.is-selected]="matrixSelection?.actionId === action.id"
                            >
                              {{ action.actionName }}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <ng-container *ngFor="let row of matrixGridRows">
                            <tr *ngIf="row.kind === 'group'" class="rs-cause-group-row">
                              <td [attr.colspan]="matrixVisibleActions.length + 1">
                                <button
                                  type="button"
                                  class="rs-cause-group-toggle"
                                  [attr.aria-expanded]="!isCauseGroupCollapsed(row.group.id)"
                                  (click)="toggleCauseGroup(row.group.id)"
                                >
                                  <mat-icon aria-hidden="true">
                                    {{ isCauseGroupCollapsed(row.group.id) ? 'chevron_right' : 'expand_more' }}
                                  </mat-icon>
                                  <span>{{ row.group.name }}</span>
                                  <span class="rs-count">{{ row.group.causes.length }}</span>
                                </button>
                              </td>
                            </tr>
                            <tr *ngIf="row.kind === 'cause'">
                              <th
                                scope="row"
                                class="rs-row-header"
                                [class.is-selected]="matrixSelection?.causeName === row.cause.name"
                              >
                                {{ row.cause.name }}
                              </th>
                              <td
                                *ngFor="let action of matrixVisibleActions; let c = index"
                                class="rs-grid-cell"
                                [class.is-group-start]="isActionGroupStart(action)"
                              >
                                <button
                                  type="button"
                                  class="rs-grid-button"
                                  [id]="getGridCellId(row.cause, action)"
                                  [class.is-on]="isPrescribed(getCell(action.id, row.cause.name))"
                                  [class.is-selected]="isGridCellSelected(row.cause, action)"
                                  [attr.tabindex]="isGridCellTabStop(row.cause, action, row.index, c) ? 0 : -1"
                                  [attr.aria-pressed]="isPrescribed(getCell(action.id, row.cause.name))"
                                  [attr.aria-label]="getCellDescription(row.cause, action)"
                                  [title]="getCellDescription(row.cause, action)"
                                  (click)="selectGridCell(row.cause, action)"
                                  (keydown)="onGridCellKeydown($event, row.cause, action)"
                                >
                                  {{ getCellGlyph(getCell(action.id, row.cause.name)) }}
                                </button>
                              </td>
                            </tr>
                          </ng-container>
                        </tbody>
                      </table>
                    </div>
                    <p class="rs-grid-legend">
                      <span>30d deadline in days</span>
                      <span>NC next cycle</span>
                      <span>↻ repeat</span>
                      <span>↓ lower risk</span>
                      <span>⊘ deactivate</span>
                      <span>Click an empty cell to prescribe · Space toggles · Arrow keys move</span>
                    </p>
                  </div>

                  <aside class="rs-inspector" aria-label="Selected cell">
                    <ng-container *ngIf="selectedCell as cell; else noCellSelected">
                      <p class="rs-inspector-context">{{ activeLevel?.name }} · {{ matrixSelection?.causeName }}</p>
                      <h3 class="rs-inspector-title">{{ selectedActionName }}</h3>
                      <mat-checkbox
                        class="rs-checkbox"
                        [checked]="isPrescribed(cell)"
                        (change)="setPrescribed(cell, $event.checked)"
                      >
                        Prescribe this action
                      </mat-checkbox>

                      <ng-container *ngIf="isPrescribed(cell); else cellNotPrescribed">
                        <ng-container *ngTemplateOutlet="cellEditor; context: { $implicit: cell }"></ng-container>

                        <div class="rs-bulk-copy">
                          <h4 class="rs-subtitle">Copy deadline and effect to prescribed cells in</h4>
                          <div class="rs-button-row">
                            <button
                              mat-button
                              type="button"
                              class="rs-button rs-button-ghost"
                              [disabled]="!countBulkCopyTargets('row')"
                              (click)="applySelectionTo('row')"
                            >
                              This row ({{ countBulkCopyTargets('row') }})
                            </button>
                            <button
                              mat-button
                              type="button"
                              class="rs-button rs-button-ghost"
                              [disabled]="!countBulkCopyTargets('column')"
                              (click)="applySelectionTo('column')"
                            >
                              This column ({{ countBulkCopyTargets('column') }})
                            </button>
                          </div>
                          <p *ngIf="bulkCopyNotice" class="rs-hint" role="status">{{ bulkCopyNotice }}</p>
                        </div>
                      </ng-container>
                      <ng-template #cellNotPrescribed>
                        <p class="rs-hint">Not prescribed. Removing a prescription also clears its deadline and effect.</p>
                      </ng-template>
                    </ng-container>
                    <ng-template #noCellSelected>
                      <p class="rs-hint">Select a cell to edit its deadline and effect on risk.</p>
                    </ng-template>
                  </aside>
                </div>
              </ng-template>
            </div>

            <!-- Action exclusions -->
            <div *ngSwitchCase="'exclusions'" class="rs-exclusions" [class.is-split]="isMaximized">
              <nav *ngIf="isMaximized" class="rs-exclusion-picker" aria-label="Prescribed action">
                <section *ngFor="let group of matrixActionGroups" [attr.aria-label]="group.name">
                  <h3 class="rs-group-title">{{ group.name }}</h3>
                  <button
                    *ngFor="let action of group.actions"
                    type="button"
                    class="rs-picker-item"
                    [class.is-active]="exclusionSourceId === action.id"
                    [attr.aria-current]="exclusionSourceId === action.id ? 'true' : null"
                    (click)="exclusionSourceId = action.id"
                  >
                    <span class="rs-picker-name">{{ action.actionName }}</span>
                    <span *ngIf="countExclusions(action.id)" class="rs-count">{{ countExclusions(action.id) }}</span>
                  </button>
                </section>
              </nav>

              <div class="rs-exclusion-body">
                <p *ngIf="!allActions.length; else exclusionEditor" class="rs-empty">
                  Configure inspections, mitigations or repairs to create exclusion rules.
                </p>

                <ng-template #exclusionEditor>
                  <div *ngIf="!isMaximized" class="rs-block">
                    <label class="rs-field-label" for="rs-exclusion-source">When this action is prescribed</label>
                    <select id="rs-exclusion-source" class="rs-select" [(ngModel)]="exclusionSourceId">
                      <optgroup *ngFor="let group of matrixActionGroups" [label]="group.name">
                        <option *ngFor="let action of group.actions" [ngValue]="action.id">
                          {{ action.actionName }}{{ countExclusions(action.id) ? ' (' + countExclusions(action.id) + ')' : '' }}
                        </option>
                      </optgroup>
                    </select>
                  </div>

                  <div class="rs-block rs-exclusion-heading">
                    <h3 class="rs-block-title">
                      {{ isMaximized ? getActionName(exclusionSourceId) + ' excludes' : 'Exclude from the plan' }}
                      ({{ countExclusions(exclusionSourceId) }})
                    </h3>
                    <p class="rs-hint">
                      Rules are one-way: excluding an action when {{ getActionName(exclusionSourceId) }} is prescribed
                      does not exclude {{ getActionName(exclusionSourceId) }} when that action is prescribed.
                    </p>
                  </div>

                  <section *ngFor="let group of matrixActionGroups" class="rs-action-group" [attr.aria-label]="group.name">
                    <h3 class="rs-group-title">{{ group.name }}</h3>
                    <ul class="rs-action-list">
                      <ng-container *ngFor="let action of group.actions">
                        <li *ngIf="action.id !== exclusionSourceId" class="rs-action-item">
                          <div class="rs-action-line">
                            <mat-checkbox
                              class="rs-checkbox rs-action-checkbox"
                              [checked]="isActionExcluded(exclusionSourceId, action.id)"
                              (change)="onExcludedActionChange(exclusionSourceId, action.id)"
                            >
                              {{ action.actionName }}
                            </mat-checkbox>
                            <span
                              *ngIf="isMutualExclusion(exclusionSourceId, action.id)"
                              class="rs-tag"
                              title="Each action excludes the other"
                            >
                              mutual
                            </span>
                          </div>
                        </li>
                      </ng-container>
                    </ul>
                  </section>

                  <div class="rs-block">
                    <h3 class="rs-block-title">All rules ({{ actionExclusionGroups.length }})</h3>
                    <ul *ngIf="actionExclusionGroups.length; else noExclusionRules" class="rs-rule-list">
                      <li *ngFor="let rule of actionExclusionGroups">
                        <button
                          type="button"
                          class="rs-rule"
                          [class.is-active]="rule.prescribedActionId === exclusionSourceId"
                          (click)="exclusionSourceId = rule.prescribedActionId"
                        >
                          <span class="rs-rule-source">{{ getActionName(rule.prescribedActionId) }}</span>
                          excludes {{ formatExcludedActions(rule.excludedActionIds) }}
                        </button>
                      </li>
                    </ul>
                    <ng-template #noExclusionRules>
                      <p class="rs-hint">No rules yet.</p>
                    </ng-template>
                  </div>
                </ng-template>
              </div>
            </div>

            <!-- Repair settings -->
            <div *ngSwitchCase="'repair'" class="rs-section-body">
              <div class="rs-columns">
                <div class="rs-block" [formGroup]="repairScopeForms">
                  <h3 class="rs-block-title" id="rs-repair-scope-title">Repair scope</h3>
                  <mat-radio-group
                    class="rs-radio-list"
                    formControlName="repairScopeControl"
                    aria-labelledby="rs-repair-scope-title"
                    (change)="onRepairSettingsChange($event)"
                  >
                    <mat-radio-button value="0">Only damaged elements</mat-radio-button>
                    <mat-radio-button value="1">Damaged and attached elements</mat-radio-button>
                    <mat-radio-button disabled value="2">
                      Damaged and attached elements, by section table
                      <span class="rs-option-hint">Not available yet</span>
                    </mat-radio-button>
                  </mat-radio-group>
                </div>

                <div class="rs-block">
                  <h3 class="rs-block-title" id="rs-renew-thickness-title">Renew thickness</h3>
                  <div class="rs-segmented" role="radiogroup" aria-labelledby="rs-renew-thickness-title">
                    <button
                      type="button"
                      role="radio"
                      class="rs-segment"
                      [class.is-active]="renewThicknessForms.value.renewThicknessControl === '0'"
                      [attr.aria-checked]="renewThicknessForms.value.renewThicknessControl === '0'"
                      (click)="setRenewThickness('0')"
                    >
                      Original
                    </button>
                    <button
                      type="button"
                      role="radio"
                      class="rs-segment"
                      [class.is-active]="renewThicknessForms.value.renewThicknessControl === '1'"
                      [attr.aria-checked]="renewThicknessForms.value.renewThicknessControl === '1'"
                      (click)="setRenewThickness('1')"
                    >
                      Rule
                    </button>
                  </div>
                  <p class="rs-hint">Thickness used for renewed plates: the original as-built value or the class rule.</p>
                </div>
              </div>

              <div class="rs-columns" [formGroup]="repairCriteriaForms">
                <div *ngFor="let group of repairCriteriaGroups" class="rs-block">
                  <h3 class="rs-block-title">{{ group.name }}</h3>
                  <div class="rs-criteria-fields">
                    <div
                      *ngFor="let field of group.fields"
                      class="rs-criterion"
                      [class.has-warning]="getCriterionWarning(field)"
                    >
                      <label class="rs-criterion-label" [for]="'rs-criterion-' + field.control">
                        {{ field.label }}
                        <span *ngIf="getCriterionRange(field) as range" class="rs-criterion-range">{{ range }}</span>
                      </label>
                      <div class="rs-input-unit">
                        <input
                          class="rs-input"
                          type="number"
                          onwheel="return false"
                          [id]="'rs-criterion-' + field.control"
                          [formControlName]="field.control"
                        />
                        <span class="rs-unit">{{ field.unit }}</span>
                      </div>
                      <p *ngIf="getCriterionWarning(field) as warning" class="rs-field-warning">{{ warning }}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Inspection points -->
            <div *ngSwitchCase="'points'" class="rs-section-body rs-columns">
              <div class="rs-block rs-block-wide" [formGroup]="inspectionTypeForm" (change)="onInspectionTypeOptionsChange()">
                <h3 class="rs-block-title">Inspection type</h3>
                <label class="rs-field-label" for="rs-inspection-type">Inspection used for prescribed points</label>
                <select id="rs-inspection-type" class="rs-select" formControlName="inspectionTypeId">
                  <option value=""></option>
                  <option *ngFor="let opt of inspectionTypeOptions" [value]="opt.id">{{ opt.label }}</option>
                </select>
              </div>

              <div class="rs-block">
                <h3 class="rs-block-title">Plates</h3>
                <mat-checkbox class="rs-checkbox" [(ngModel)]="_AutomaticPoints.PlatesSettings.PositionGaugingsforPlates">
                  Position gauging points on plates
                </mat-checkbox>

                <ng-container *ngIf="_AutomaticPoints.PlatesSettings.PositionGaugingsforPlates">
                  <h4 class="rs-subtitle">Positioning criteria</h4>
                  <table class="rs-criteria-table">
                    <thead>
                      <tr>
                        <th scope="col"></th>
                        <th scope="col">Area (m²)</th>
                        <th scope="col">Points</th>
                        <th scope="col">Center</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr *ngFor="let PlatesPointCriteria of _AutomaticPoints.PlatesSettings.PositionPointCriteriaList; let i = index">
                        <td class="rs-criteria-prefix">over</td>
                        <td>
                          <input
                            class="rs-input"
                            type="number"
                            min="0.09"
                            max="200"
                            step="0.5"
                            [attr.aria-label]="'Plates criterion ' + (i + 1) + ' area'"
                            [(ngModel)]="PlatesPointCriteria.Area"
                          />
                        </td>
                        <td>
                          <input
                            class="rs-input"
                            type="number"
                            min="1"
                            max="10"
                            [attr.aria-label]="'Plates criterion ' + (i + 1) + ' number of points'"
                            [(ngModel)]="PlatesPointCriteria.NumberOfPoints"
                          />
                        </td>
                        <td>
                          <input
                            class="rs-native-checkbox"
                            type="checkbox"
                            [attr.aria-label]="'Plates criterion ' + (i + 1) + ' add center point'"
                            [(ngModel)]="PlatesPointCriteria.AddCenterPoint"
                          />
                        </td>
                      </tr>
                    </tbody>
                  </table>

                  <h4 class="rs-subtitle" id="rs-plates-arrangement-title">Points arrangement</h4>
                  <mat-radio-group
                    class="rs-arrangements"
                    aria-labelledby="rs-plates-arrangement-title"
                    [(ngModel)]="PointsArrangementsPlateString"
                    (ngModelChange)="onPlatesPointsArrangementsChange($event)"
                  >
                    <mat-radio-button value="1" class="rs-arrangement">
                      <img src="./statics/plates/Plate1.jpg" alt="" />
                      <span>Ascending diagonal</span>
                    </mat-radio-button>
                    <mat-radio-button value="2" class="rs-arrangement">
                      <img src="./statics/plates/Plate2.jpg" alt="" />
                      <span>Descending diagonal</span>
                    </mat-radio-button>
                    <mat-radio-button value="0" class="rs-arrangement">
                      <img src="./statics/plates/Plate3.jpg" alt="" />
                      <span>Linear</span>
                    </mat-radio-button>
                    <mat-radio-button value="4" class="rs-arrangement">
                      <img src="./statics/plates/Plate4.jpg" alt="" />
                      <span>Zig-zag</span>
                    </mat-radio-button>
                    <mat-radio-button value="5" class="rs-arrangement">
                      <img src="./statics/plates/Plate6.jpg" alt="" />
                      <span>On vertices</span>
                    </mat-radio-button>
                  </mat-radio-group>
                </ng-container>
              </div>

              <div class="rs-block">
                <h3 class="rs-block-title">Stiffeners</h3>
                <mat-checkbox
                  class="rs-checkbox"
                  [(ngModel)]="_AutomaticPoints.StiffenersSettings.PositionGaugingPointsOnStiffeners"
                >
                  Position gauging points on stiffeners
                </mat-checkbox>

                <ng-container
                  *ngIf="_AutomaticPoints.StiffenersSettings.PositionGaugingPointsOnStiffeners || _AutomaticPoints.StiffenersSettings.PositionGaugingPointsOnBays"
                >
                  <h4 class="rs-subtitle">Positioning criteria</h4>
                  <table class="rs-criteria-table">
                    <thead>
                      <tr>
                        <th scope="col"></th>
                        <th scope="col">Length (m)</th>
                        <th scope="col">Points</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr *ngFor="let InspectionAppliedStiffeners of _AutomaticPoints.StiffenersSettings.PositionPointCriteriaList; let i = index">
                        <td class="rs-criteria-prefix">over</td>
                        <td>
                          <input
                            class="rs-input"
                            type="number"
                            min="0.9"
                            max="100"
                            step="0.5"
                            [attr.aria-label]="'Stiffeners criterion ' + (i + 1) + ' length'"
                            [(ngModel)]="InspectionAppliedStiffeners.Length"
                          />
                        </td>
                        <td>
                          <input
                            class="rs-input"
                            type="number"
                            min="1"
                            max="10"
                            [attr.aria-label]="'Stiffeners criterion ' + (i + 1) + ' number of points'"
                            [(ngModel)]="InspectionAppliedStiffeners.NumberOfPoints"
                          />
                        </td>
                      </tr>
                    </tbody>
                  </table>

                  <h4 class="rs-subtitle">Stiffener parts</h4>
                  <div class="rs-checkbox-list">
                    <mat-checkbox class="rs-checkbox" [(ngModel)]="_AutomaticPoints.StiffenersSettings.InsertPointsOnWeb">
                      Insert points on web
                    </mat-checkbox>
                    <mat-checkbox class="rs-checkbox" [(ngModel)]="_AutomaticPoints.StiffenersSettings.InsertPointsOnFlange">
                      Insert points on flange
                    </mat-checkbox>
                  </div>
                </ng-container>
              </div>

              <div class="rs-block">
                <h3 class="rs-block-title">Face plates</h3>
                <mat-checkbox
                  class="rs-checkbox"
                  [(ngModel)]="_AutomaticPoints.FacePlatesSettings.PositionGaugingPointsOnFacePlates"
                >
                  Position gauging points on face plates
                </mat-checkbox>

                <ng-container *ngIf="_AutomaticPoints.FacePlatesSettings.PositionGaugingPointsOnFacePlates">
                  <h4 class="rs-subtitle">Positioning criteria</h4>
                  <table class="rs-criteria-table">
                    <thead>
                      <tr>
                        <th scope="col"></th>
                        <th scope="col">Length (m)</th>
                        <th scope="col">Points</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr *ngFor="let InspectionAppliedFacePlates of _AutomaticPoints.FacePlatesSettings.PositionPointCriteriaList; let i = index">
                        <td class="rs-criteria-prefix">over</td>
                        <td>
                          <input
                            class="rs-input"
                            type="number"
                            min="0.01"
                            max="100"
                            step="0.5"
                            [attr.aria-label]="'Face plates criterion ' + (i + 1) + ' length'"
                            [(ngModel)]="InspectionAppliedFacePlates.Length"
                          />
                        </td>
                        <td>
                          <input
                            class="rs-input"
                            type="number"
                            min="1"
                            max="10"
                            [attr.aria-label]="'Face plates criterion ' + (i + 1) + ' number of points'"
                            [(ngModel)]="InspectionAppliedFacePlates.NumberOfPoints"
                          />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </ng-container>
              </div>

              <div class="rs-block">
                <h3 class="rs-block-title">Brackets</h3>
                <mat-checkbox
                  class="rs-checkbox"
                  [(ngModel)]="_AutomaticPoints.BracketSettings.PositionGaugingPointsOnBrackets"
                >
                  Position gauging points on brackets
                </mat-checkbox>

                <ng-container *ngIf="_AutomaticPoints.BracketSettings.PositionGaugingPointsOnBrackets">
                  <h4 class="rs-subtitle">Positioning criteria</h4>
                  <table class="rs-criteria-table">
                    <thead>
                      <tr>
                        <th scope="col">Area (m²)</th>
                        <th scope="col">Web pts</th>
                        <th scope="col">Flange pts</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr *ngFor="let _PositionPointCriteriaBrackets of _AutomaticPoints.BracketSettings.PositionPointCriteriaList; let i = index">
                        <td>
                          <input
                            class="rs-input"
                            type="number"
                            min="0.5"
                            max="100"
                            step="0.5"
                            [attr.aria-label]="'Brackets criterion ' + (i + 1) + ' area'"
                            [(ngModel)]="_PositionPointCriteriaBrackets.Area"
                          />
                        </td>
                        <td>
                          <input
                            class="rs-input"
                            type="number"
                            min="1"
                            max="10"
                            [attr.aria-label]="'Brackets criterion ' + (i + 1) + ' web points'"
                            [(ngModel)]="_PositionPointCriteriaBrackets.NumberOfPointsWeb"
                          />
                        </td>
                        <td>
                          <input
                            class="rs-input"
                            type="number"
                            min="1"
                            max="10"
                            [attr.aria-label]="'Brackets criterion ' + (i + 1) + ' flange points'"
                            [(ngModel)]="_PositionPointCriteriaBrackets.NumberOfPointsFlange"
                          />
                        </td>
                      </tr>
                    </tbody>
                  </table>

                  <h4 class="rs-subtitle" id="rs-brackets-arrangement-title">Points arrangement</h4>
                  <mat-radio-group
                    class="rs-arrangements"
                    aria-labelledby="rs-brackets-arrangement-title"
                    [(ngModel)]="PointsArrangementsBracketString"
                    (ngModelChange)="onBracketsPointsArrangementsChange($event)"
                  >
                    <mat-radio-button value="2" class="rs-arrangement">
                      <img src="./statics/bracket/Bracket1.jpg" alt="" />
                      <span>Descending diagonal</span>
                    </mat-radio-button>
                    <mat-radio-button value="5" class="rs-arrangement">
                      <img src="./statics/bracket/Bracket2.jpg" alt="" />
                      <span>On vertices</span>
                    </mat-radio-button>
                    <mat-radio-button value="6" class="rs-arrangement">
                      <img src="./statics/bracket/Bracket3.jpg" alt="" />
                      <span>On edges</span>
                    </mat-radio-button>
                  </mat-radio-group>
                </ng-container>
              </div>
            </div>

            <!-- Post-RBA analysis -->
            <div *ngSwitchCase="'postRba'" class="rs-section-body rs-narrow">
              <div class="rs-block">
                <h3 class="rs-block-title">Analyses ({{ selectedPostRbaProperties.length }})</h3>
                <ul *ngIf="selectedPostRbaProperties.length > 0; else noPostRbaMetrics" class="rs-chip-list">
                  <li *ngFor="let propertyId of selectedPostRbaProperties" class="rs-chip">
                    <span>{{ getSelectedPostRbaPropertyName(propertyId) }}</span>
                    <button
                      type="button"
                      class="rs-chip-remove"
                      [attr.aria-label]="'Remove ' + getSelectedPostRbaPropertyName(propertyId)"
                      (click)="removePostRbaProperty(propertyId)"
                    >
                      <mat-icon>close</mat-icon>
                    </button>
                  </li>
                </ul>
                <ng-template #noPostRbaMetrics>
                  <p class="rs-hint">No analysis selected.</p>
                </ng-template>

                <ng-container *ngIf="availablePostRbaMetrics.length">
                  <label class="rs-field-label rs-add-label" id="rs-post-rba-metric-label">Add analysis</label>
                  <mat-form-field appearance="outline" class="rs-form-field" subscriptSizing="dynamic">
                    <mat-select
                      aria-labelledby="rs-post-rba-metric-label"
                      placeholder="Select a metric"
                      [(ngModel)]="selectedPostRbaProperty"
                      (selectionChange)="addPostRbaProperty(); scheduleStateRefresh()"
                    >
                      <mat-option *ngFor="let metric of availablePostRbaMetrics" [value]="metric.propertyId">
                        {{ metric.name }}
                      </mat-option>
                    </mat-select>
                  </mat-form-field>
                </ng-container>
              </div>

              <div class="rs-block">
                <h3 class="rs-block-title">Run after plan approval</h3>
                <label class="rs-field-label" for="rs-post-rba-offset">Time after the plan is approved</label>
                <div class="rs-inline-field">
                  <input
                    id="rs-post-rba-offset"
                    class="rs-input rs-input-offset"
                    type="number"
                    min="0"
                    step="1"
                    placeholder="0"
                    [(ngModel)]="analysisOffsetDisplayValue"
                    (ngModelChange)="onAnalysisOffsetDisplayValueChange()"
                  />
                  <div class="rs-segmented rs-time-unit-selector" role="radiogroup" aria-label="Time unit">
                    <button
                      *ngFor="let unit of timeUnits"
                      type="button"
                      role="radio"
                      class="rs-segment"
                      [class.is-active]="analysisOffsetTimeUnit === unit"
                      [attr.aria-checked]="analysisOffsetTimeUnit === unit"
                      (click)="setAnalysisOffsetTimeUnit(unit)"
                    >
                      {{ unit }}
                    </button>
                  </div>
                </div>
                <p class="rs-hint">Stored as {{ analysisOffsetDays }} days.</p>
              </div>
            </div>

            <!-- Scope -->
            <div *ngSwitchCase="'scope'" class="rs-section-body rs-narrow">
              <div class="rs-scope-header">
                <span class="rs-scope-count" [class.is-warning]="!scopeSectionTotals.selected">
                  {{ scopeSectionTotals.selected }} of {{ scopeSectionTotals.total }} sections selected
                </span>
              </div>
              <p class="rs-hint">
                Lists the regions visible in the 3D viewer when the window opened. Only the selected sections are saved.
              </p>
              <div class="rs-region-select">
                <phdsoft-region-select
                  [Regions]="_AutomaticPoints.GeneralSettings.Regions"
                  (SelectRegion)="onSelectRegion($event)"
                  (SelectSection)="onSelectSection($event)"
                ></phdsoft-region-select>
              </div>
            </div>
          </div>
        </section>
      </div>

      <footer class="rs-save-bar">
        <span class="rs-save-status" role="status" aria-live="polite" [class.is-dirty]="hasUnsavedChanges">
          {{ saveStatusText }}
        </span>
        <button
          mat-button
          type="button"
          class="rs-button rs-button-ghost"
          [disabled]="!hasUnsavedChanges || isSaving"
          (click)="requestDiscard()"
        >
          Discard
        </button>
        <button
          mat-raised-button
          type="button"
          class="rs-button rs-button-primary"
          [disabled]="isSaving"
          (click)="saveRiskShieldConfiguration()"
        >
          <mat-spinner *ngIf="isSaving" diameter="16"></mat-spinner>
          <span>Save</span>
        </button>
      </footer>

      <div *ngIf="confirmation as dialog" class="rs-confirm-backdrop" (keydown.escape)="cancelPendingAction()">
        <div class="rs-confirm" role="alertdialog" aria-modal="true" aria-labelledby="rs-confirm-title" aria-describedby="rs-confirm-text">
          <h2 id="rs-confirm-title" class="rs-confirm-title">{{ dialog.title }}</h2>
          <p id="rs-confirm-text" class="rs-confirm-text">{{ dialog.text }}</p>
          <div class="rs-confirm-actions">
            <button mat-button type="button" class="rs-button rs-button-ghost" (click)="cancelPendingAction()">
              {{ dialog.destructive ? 'Keep editing' : 'Cancel' }}
            </button>
            <button
              mat-raised-button
              type="button"
              class="rs-button"
              [class.rs-button-danger]="dialog.destructive"
              [class.rs-button-primary]="!dialog.destructive"
              (click)="confirmPendingAction()"
            >
              {{ dialog.action }}
            </button>
          </div>
        </div>
      </div>
    </ng-template>
  </div>
</phdsoft-sideboard>

<ng-template #sectionList let-variant>
  <ul class="rs-section-list" [class.is-rail]="variant === 'rail'">
    <li *ngFor="let section of navigationSections">
      <button
        type="button"
        class="rs-section-item"
        [class.is-active]="variant === 'rail' && section.id === activeSection"
        [attr.aria-current]="variant === 'rail' && section.id === activeSection ? 'true' : null"
        (click)="openSection(section.id)"
      >
        <span class="rs-status-dot" [ngClass]="'is-' + summaries[section.id].status" aria-hidden="true"></span>
        <span class="rs-section-text">
          <span class="rs-section-name">{{ section.name }}</span>
          <span class="rs-section-summary">{{ summaries[section.id].text }}</span>
        </span>
        <mat-icon *ngIf="variant === 'hub'" class="rs-section-chevron" aria-hidden="true">chevron_right</mat-icon>
      </button>
    </li>
  </ul>
</ng-template>

<ng-template #cellEditor let-cell>
  <div class="rs-cell-editor">
    <div class="rs-editor-field">
      <span class="rs-field-label">Deadline</span>
      <div class="rs-inline-field">
        <div class="rs-segmented" role="radiogroup" aria-label="Deadline type">
          <button
            *ngFor="let mode of deadlineModes"
            type="button"
            role="radio"
            class="rs-segment"
            [class.is-active]="getDeadlineMode(cell) === mode.id"
            [attr.aria-checked]="getDeadlineMode(cell) === mode.id"
            (click)="setDeadlineMode(cell, mode.id)"
          >
            {{ mode.label }}
          </button>
        </div>
        <label *ngIf="getDeadlineMode(cell) === 'days'" class="rs-days">
          <input
            class="rs-input rs-input-compact"
            type="number"
            min="0"
            step="1"
            placeholder="–"
            onwheel="return false"
            aria-label="Deadline in days"
            [formControl]="getDeadlineControl(cell)"
          />
          <span>days</span>
        </label>
      </div>
    </div>

    <div class="rs-editor-field" [class.is-disabled]="!hasDeadline(cell)">
      <mat-checkbox
        class="rs-checkbox"
        [checked]="cell.value.repeat"
        [disabled]="!hasDeadline(cell)"
        (change)="setRepeat(cell, $event.checked)"
      >
        Repeat every cycle
      </mat-checkbox>
      <p *ngIf="!hasDeadline(cell)" class="rs-hint">Available when a deadline is set.</p>
    </div>

    <div class="rs-editor-field">
      <span class="rs-field-label">Effect on risk</span>
      <div class="rs-segmented rs-segmented-full" role="radiogroup" aria-label="Effect on risk">
        <button
          *ngFor="let effect of effectOptions"
          type="button"
          role="radio"
          class="rs-segment"
          [class.is-active]="getCellEffect(cell) === effect.id"
          [attr.aria-checked]="getCellEffect(cell) === effect.id"
          (click)="setEffect(cell, effect.id)"
        >
          {{ effect.label }}
        </button>
      </div>
      <p class="rs-hint">{{ getEffectHint(cell) }}</p>
    </div>
  </div>
</ng-template>
